/**
 * Interactive inactivity gate.
 *
 * Reconciles two goals that seem to conflict:
 *   1. Sign the *interactive* user out after 30 minutes of no meaningful
 *      interaction (screens, taps, keyboard).
 *   2. Keep background location sharing and proximity alerts working the
 *      whole time — pausing them for a 30-min inactivity nap would break
 *      the whole "3 mile heads-up" promise of the app.
 *
 * The way this works:
 *   • `session_token` in localStorage is the *long-lived* auth token
 *     (30-day TTL server-side). Background geolocation reads this to
 *     POST /api/locations/update while the phone is locked.
 *   • Alongside it we track `interactive_last_seen_at` — a client-only
 *     wall-clock timestamp we bump every time the user meaningfully
 *     interacts with a protected screen.
 *   • On protected-screen mount + every user event, we compare
 *     `now - interactive_last_seen_at` against 30 minutes. If exceeded,
 *     we redirect to /signin — but we do NOT delete `session_token`, so
 *     the background service keeps working.
 *   • When the user signs back in, `interactive_last_seen_at` resets and
 *     they land back on the map with everything intact.
 *
 * Explicit sign-out (the "Sign out" button) still deletes the session
 * token and the last-seen timestamp so both interactive AND background
 * stop, exactly as the user asked.
 */
import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const KEY_LAST_SEEN = "interactive_last_seen_at";
const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export function touchInteractiveSession() {
  try {
    localStorage.setItem(KEY_LAST_SEEN, String(Date.now()));
  } catch {
    /* storage disabled — non-fatal */
  }
}

export function isInteractiveExpired() {
  try {
    const raw = localStorage.getItem(KEY_LAST_SEEN);
    if (!raw) return false; // first visit — not expired
    const last = Number(raw);
    if (!Number.isFinite(last)) return false;
    return Date.now() - last > TIMEOUT_MS;
  } catch {
    return false;
  }
}

export function clearInteractiveSession() {
  try {
    localStorage.removeItem(KEY_LAST_SEEN);
  } catch {}
}

/**
 * Mount this hook once inside the authenticated app shell. It:
 *   - Redirects to /signin when the last-seen timestamp is older than 30 min.
 *   - Refreshes the timestamp on any meaningful interaction (click, keydown,
 *     touchstart, visibility change to visible).
 *   - Does NOT delete the auth token — background tracking survives.
 */
export function useInactivityGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const lastCheckRef = useRef(0);

  useEffect(() => {
    const checkAndMaybeRedirect = () => {
      if (isInteractiveExpired()) {
        navigate(`/signin?next=${encodeURIComponent(location.pathname + location.search)}`, {
          replace: true,
        });
      } else {
        touchInteractiveSession();
      }
    };

    // Initial check on mount / route change
    checkAndMaybeRedirect();

    // Throttled event handler — we don't need to persist to localStorage on
    // every mousemove tick.
    const onActivity = () => {
      const now = Date.now();
      if (now - lastCheckRef.current < 5_000) return; // 5-second throttle
      lastCheckRef.current = now;
      checkAndMaybeRedirect();
    };

    const events = ["pointerdown", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    const onVisibility = () => {
      if (document.visibilityState === "visible") checkAndMaybeRedirect();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
}
